import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { c } from 'ttag';

import {
    AuthenticationStore,
    Button,
    LegalDocument,
    LoaderPage,
    getLegalDocument,
    useAuthentication,
    useI18n,
    useInsideApi,
    useLocalState,
} from '@freelancelabs/components';
import { LogoFreelance } from '@freelancelabs/icons';
import { AppName, acceptCgu, getUser, redirectToApp, removeItem } from '@freelancelabs/shared';

import { fetchMfaRequirement } from '../../api/mfaRequirement';
import { AlertType, PublicRoutes, redirectAfterLoginKey } from '../../constants';
import MfaSetupPage from './MfaSetup';

interface AppScope {
    appName: AppName | string;
    isConfirmed: boolean;
}

const ALLOWED_STATES = ['FORCE_CHANGE_PASSWORD', 'CONFIRMED', 'NEW_PASSWORD_REQUIRED'];

/**
 * Les noms de portée qui ouvrent Client Hub.
 *
 * Attention, ces noms ne viennent PAS de `AppName` : cette énumération décrit nos
 * applications front, avec leur sous-domaine et leur port, alors qu'ici on lit le
 * registre d'applications d'`inside-api`, où un nom est associé à un client
 * Cognito par le paramètre SSM `APP_CLIENT_IDS`. Les deux vocabulaires se
 * ressemblent et ne sont pas le même, et c'est ce qui a déjà fait refuser à la
 * connexion des personnes pourtant habilitées.
 *
 * **`ACCOUNT` est celui que portent réellement les gens** (PCG-117), et son
 * absence était le défaut. Mesuré sur le registre du stage develop, **avant le
 * renommage du 2026-08-22** : la seconde ligne s'y lit désormais `CLIENT_HUB_API`,
 * et le relevé est laissé tel qu'il a été pris.
 *
 *     ACCOUNT:5sosu410j2bu3r8vo9m60uqb41
 *     INSIDE_CLIENT_API:760jkoa5t5u9bgjt5ttbr9fe41
 *
 * Les trois applications front partagent un seul client Cognito, celui d'`ACCOUNT`
 * (voir `BackofficeGuard`, qui en tire la conséquence inverse : une portée ne
 * sépare pas le portail du back-office). Une connexion écrit donc toujours une
 * portée `ACCOUNT`, et jamais autre chose.
 *
 * Attention au piège qui a masqué le défaut si longtemps : `INSIDE_CLIENT_API` est
 * un client `client_credentials`, celui de notre back end. **Personne ne s'y
 * authentifie.** Si les utilisateurs du portail passaient, c'est que cette portée
 * leur avait été écrite par notre serveur au moment de l'activation, la lambda
 * `confirm-user` indexant par le client de l'appelant. Toute personne arrivée
 * autrement, un administrateur back-office par exemple, était refusée.
 *
 * Les deux autres noms restent acceptés : les retirer mettrait dehors du jour au
 * lendemain les comptes qui ne portent qu'eux.
 *
 * **Attention, `CLIENT_HUB_API` ET `INSIDE_CLIENT_API` SONT LA MÊME PORTÉE, sous ses
 * deux noms, et c'est pourquoi la liste en compte quatre.** Le renommage du produit
 * (2026-08-22) a changé le nom que `inside-api` sert désormais, mais **il n'a pas
 * réécrit ce qui est déjà stocké sur les comptes** : une portée posée à l'activation y
 * reste sous son nom d'alors. Substituer le nouveau à l'ancien aurait donc mis dehors
 * exactement les comptes que la phrase ci-dessus protège, et personne ne l'aurait vu
 * avant qu'un utilisateur ne se plaigne. L'ancien nom part le jour où plus aucun compte
 * ne le porte, ce qui se mesure et ne se suppose pas.
 *
 * Ce que cette garde dit est « cette personne a-t-elle le droit de se connecter »,
 * jamais « à quelle application ». L'admission dans une application est vérifiée
 * par sa propre garde, sur le `role.scope` que rend `viewer`, et la frontière
 * réelle est l'API, qui refuse la donnée hors périmètre.
 */
const PORTAL_SCOPE_NAMES: string[] = ['ACCOUNT', 'INSIDE_CLIENT_API', 'CLIENT_HUB_API', AppName.PORTAIL_CLIENT];

const RedirectMiddlewarePage = () => {
    const navigate = useNavigate();
    const { logout, getAuthorizationToken, fetchMfaSettings, isFederatedSession } =
        useAuthentication() as AuthenticationStore;
    const api = useInsideApi();
    const { localeCode } = useI18n();
    const [lastCgu, setLastCgu] = useState<LegalDocument>();
    const [isLoading, setIsLoading] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [redirectAfterLogin] = useLocalState('', redirectAfterLoginKey);
    /** La barriere du MFA est tombee : l'ecran de pose remplace cette page. */
    const [mfaRequired, setMfaRequired] = useState(false);
    /**
     * L'adresse de la personne, retenue pour nommer l'entree dans son application
     * d'authentification. Sans elle le libelle serait « Freelance.com: », et quelqu'un
     * qui gere deux comptes ne saurait pas lequel est lequel.
     */
    const [email, setEmail] = useState('');

    const reject = async () => {
        /**
         * **La destination est oubliée ici aussi, pas seulement quand on la suit**
         * (PCG-119).
         *
         * Elle n'était consommée que par `redirect`, donc un refus la laissait en
         * `localStorage`. La connexion réussie suivante la reprenait, et ce n'est
         * pas forcément la même personne : sur un poste partagé, quelqu'un tente
         * d'ouvrir le back-office, se fait refuser, et la personne d'après se
         * retrouve envoyée là-bas sans l'avoir demandé.
         *
         * Constaté ce soir dans l'ordre inverse, et c'est ce qui l'a rendu visible :
         * des refus successifs ont laissé l'adresse du back-office derrière eux,
         * puis un client s'y est fait expédier des que la connexion a marché.
         *
         * Une intention de navigation qui s'est terminée par un refus est finie.
         * La garder, c'est la faire porter par la tentative suivante.
         */
        removeItem(redirectAfterLoginKey);
        await logout();
        navigate(PublicRoutes.LOGIN, {
            replace: true,
            state: {
                withAlert: AlertType.APP_UNAUTHORIZED,
            },
        });
    };

    /**
     * LA BARRIERE DU MFA, POSEE ICI ET NULLE PART AILLEURS (PCG-303).
     *
     * C'est la seule couture par laquelle les DEUX chemins passent : l'effet quand les
     * CGU sont deja acceptees, et `accept()` quand elles viennent de l'etre. La poser
     * dans l'effet seul laisserait entrer quiconque accepte les CGU aujourd'hui.
     *
     * **Apres les CGU et pas avant**, parce qu'accepter les conditions est ce qui
     * autorise a utiliser le service, alors que le MFA regle la maniere de s'y
     * connecter : demander un facteur a quelqu'un qui n'a pas encore accepte les
     * conditions lui ferait poser un secret pour un service dont il peut encore
     * refuser l'usage.
     *
     * **Deux faits et non un** : l'exigence vient de l'API (une politique de compte),
     * le facteur deja pose vient du pool. Aucun des deux ne se deduit de l'autre, et
     * c'est pour ca qu'ils sont lus separement.
     *
     * **Elle echoue OUVERTE**, comme `fetchMfaRequirement` le documente : un echec de
     * lecture laisse passer, la demande etant reportee au chargement suivant plutot
     * que de dresser un mur devant `/account`, l'ecran meme ou l'on pose un facteur.
     */
    const mfaBlocks = async (federated: boolean) => {
        /**
         * PAS DE MFA SI C'EST DU SSO (PCG-304), decide par Mohamed le 2026-08-28. Le
         * fournisseur d'identite a deja demande son second facteur ; en reclamer un
         * deuxieme ici est une double contrainte sur le meme trajet.
         *
         * **Attention, C'EST UN FAIT DE LA SESSION ET NON DE LA PERSONNE.** La meme personne
         * revenue par mot de passe retombe sous la barriere, et c'est voulu : ce qui la
         * dispense est le facteur demande sur CE chemin-la. Lire la dispense sur un attribut
         * de la personne l'exempterait aussi des connexions qui n'ont rien prouve.
         *
         * **Ce n'est pas la meme chose que « un interne ne porte pas de rattachement
         * client ».** `viewer.mfaRequired` se derive des rattachements clients, et le
         * declencheur `membership_single_root_check` en interdit un a un interne : la
         * dispense est donc deja vraie par construction aujourd'hui. Ce qui ne la rend pas
         * ECRITE. Le jour ou un interne porterait un rattachement client, la derivation
         * cesse de proteger et seule cette ligne tient.
         *
         * **Le fait est LU UNE FOIS par `redirect` et passe ici**, plutot que relu : la
         * destination le lit aussi, et deux lectures de la meme session pourraient se
         * contredire sur une seule navigation. Le recevoir en argument est aussi ce qui
         * empeche cette garde de devenir inatteignable derriere un test identique place plus
         * haut, la forme exacte du defaut de PCG-63.
         *
         * `isFederatedSession` repond `false` quand elle ne sait pas : une lecture ratee ne
         * fait pas sauter une barriere.
         */
        if (federated) {
            return false;
        }

        const token = await getAuthorizationToken();
        if (!token) {
            return false;
        }
        const { required } = await fetchMfaRequirement(token);
        if (!required) {
            return false;
        }
        try {
            const { totp } = await fetchMfaSettings();
            return !totp;
        } catch {
            // Le pool n'a pas repondu : on ne bloque pas sur une lecture ratee, meme
            // raison qu'au-dessus.
            return false;
        }
    };

    /**
     * Le depart reel, sans controle : c'est ce qu'appelle l'ecran de pose du facteur
     * quand il vient d'en verifier un. Repasser par la porte demanderait au pool de
     * confirmer un enregistrement de la seconde precedente, et une lecture en retard
     * renverrait la personne sur l'ecran qu'elle vient de finir.
     */
    const goToApp = (federated = false) => {
        if (redirectAfterLogin) {
            // La destination est consommee : sans ce retrait, elle reste en
            // localStorage et renvoie indefiniment vers la meme application, y
            // compris quand la personne revient par account directement.
            // removeItem plutot que le setter du hook, qui ecrit dans un
            // useEffect ne s'executant pas avant la navigation.
            removeItem(redirectAfterLoginKey);
            window.location.replace(redirectAfterLogin);
            return;
        }
        /**
         * UN INTERNE VA SUR HUB ADMIN (PCG-304), decide par Mohamed le 2026-08-28, et le
         * « pour l'instant » de sa phrase fait partie de la decision.
         *
         * **La destination se lit sur la session federee**, faute de mieux aujourd'hui :
         * seuls les internes entrent par le SSO. C'est exactement le raccourci « SSO =
         * interne » qui a fait ECARTER la convention en dur sur `@freelance.com` pour la
         * decouverte, et il est repris ici en connaissance de cause, parce qu'il n'y a rien
         * d'autre a lire a ce moment : le role vit dans `client-hub-api`, que cette
         * application n'interroge pas.
         *
         * Il devient faux au premier client qui apporte son propre fournisseur d'identite.
         * C'est pour cela que la lecture est ICI et a un seul endroit : le jour ou la
         * destination doit se decider sur le role plutot que sur le chemin d'entree, ca coute
         * cette ligne.
         *
         * Attention, `redirectAfterLogin` PASSE AVANT, et l'ordre compte : quelqu'un qui
         * ouvrait le portail et s'est fait renvoyer ici doit revenir la ou il allait, meme
         * s'il entre par le SSO.
         */
        redirectToApp(federated ? AppName.CLIENT_BO : AppName.PORTAIL_CLIENT);
    };

    const redirect = async () => {
        // Lue UNE fois et passee aux deux, plutot que deux appels : c'est le meme fait, et
        // deux lectures pourraient se contredire sur la meme navigation.
        const federated = await isFederatedSession();
        if (await mfaBlocks(federated)) {
            setMfaRequired(true);
            return;
        }
        goToApp(federated);
    };

    const accept = async () => {
        if (lastCgu === undefined) {
            return;
        }
        setIsLoading(true);
        await api(acceptCgu({ versionName: lastCgu.version }));
        // Attendu : `redirect` interroge desormais deux services avant de partir, et
        // relacher le bouton avant sa reponse rendrait la page cliquable pendant que
        // la barriere se decide.
        await redirect();
        setIsLoading(false);
    };

    useEffect(() => {
        const run = async () => {
            const locale = localeCode.slice(0, 2).toLowerCase() as 'en' | 'fr'; // @later mv to shared i18n
            const cgu = getLegalDocument({ type: 'cgu', locale });
            const user = await api(getUser());
            setEmail(user.email ?? '');
            if (!ALLOWED_STATES.includes(user.state)) {
                await reject();
                return;
            }
            // Un appScope est indexe par app client Cognito, et les trois
            // applications front partagent le meme : ce controle dit donc « cette
            // personne a-t-elle le droit de se connecter », pas « a quelle
            // application ». C'est le bon niveau ici, et il n'y a rien a affiner :
            // le choix de la destination vient de redirect-after-login, et
            // l'admission dans une application est verifiee par sa propre garde,
            // sur le role.scope que renvoie viewer (PORTAL ou BACKOFFICE).
            /**
             * **Toutes les portées qui ouvrent, pas la première rencontrée.**
             *
             * Une personne en porte plusieurs, et elles ne se valent pas : celle
             * qu'écrit notre parcours d'activation est confirmée, celle qu'écrit
             * une simple connexion ne l'est pas. Prendre la première du tableau
             * laissait l'ordre de la réponse décider de l'admission, et refusait
             * quelqu'un dont une AUTRE portée était pourtant confirmée.
             *
             * Le défaut ne se voyait pas tant que la liste ne contenait que des
             * noms qu'une seule portée pouvait porter. Il devient atteignable dès
             * qu'`ACCOUNT` y entre, puisque tout le monde finit par en avoir une.
             */
            const portalScopes = user.appScopes.filter((appScope: AppScope) =>
                PORTAL_SCOPE_NAMES.includes(appScope.appName)
            );
            // Deux refus distincts, et ils ne disent pas la même chose : aucune
            // portée, c'est une personne étrangère à Client Hub ; une portée
            // non confirmée, c'est une activation jamais terminée.
            if (portalScopes.length === 0) {
                await reject();
                return;
            }
            if (!portalScopes.some((appScope: AppScope) => appScope.isConfirmed)) {
                await reject();
                return;
            }
            setLastCgu(cgu);
            if (user.termsAcceptance === cgu.version) {
                await redirect();
                return;
            }
            setIsInitialized(true);
        };
        void run();
    }, []);

    /**
     * La barriere passe AVANT le chargement et avant les CGU dans le rendu, parce
     * qu'a ce stade les deux sont derriere nous : on n'arrive ici que par `redirect`,
     * donc CGU reglees. Rendre l'ecran de pose plutot que naviguer vers une route
     * evite qu'un retour arriere du navigateur ne saute la barriere.
     */
    if (mfaRequired) {
        return <MfaSetupPage accountName={email} onDone={goToApp} />;
    }

    return (
        <>
            {!isInitialized ? (
                <LoaderPage />
            ) : (
                <main>
                    <div className="flex justify-center p-6">
                        <LogoFreelance size={96} />
                    </div>
                    <div className="pb-12 pt-6">
                        <div className="max-w-3xl w-full mx-auto pb-6 px-6">
                            <h1 className="text-base text-text-800">
                                {c('CGU')
                                    .t`To take advantage of our online services, you must first accept their general conditions of use.`}
                            </h1>
                        </div>
                        <div className="max-w-3xl w-full mx-auto px-6 pb-6">
                            <hr />
                            <article className="flex flex-col items-center">
                                {lastCgu?.document && (
                                    <div
                                        className="prose prose-sm md:prose-base max-w-full break-words"
                                        dangerouslySetInnerHTML={{ __html: lastCgu.document }}
                                    />
                                )}
                            </article>
                        </div>
                    </div>
                    <div className="sticky bottom-0 border-t bg-background-norm">
                        <div className="max-w-3xl w-full mx-auto px-6 py-4 flex items-center gap-6 justify-between">
                            <Button onClick={logout} category="weak">
                                {c('CGU').t`Decline and exit`}
                            </Button>
                            <Button onClick={accept} loading={isLoading}>
                                {c('CGU').t`I agree`}
                            </Button>
                        </div>
                    </div>
                </main>
            )}
        </>
    );
};

export default RedirectMiddlewarePage;
